import {useOpenCampaignsQuery} from "@/api/campaignApi";
import {useCurrentCompanyId} from "@/api/companyApi";
import {INFLUENCER_KEYS, useInfluencerListQuery, useUpdateInfluencerFilterMutation} from "@/api/influencerApi";
import DownUpArrowIcon from "@/assets/colorable/down_up_arrow.svg?react";
import FolderPlusIcon from "@/assets/colorable/folder_plus.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import Button from "@/components/Common/Button";
import AdvancedFiltersButton from "@/components/Common/filter/AdvancedFiltersButton";
import SavedFiltersButton from "@/components/Common/filter/SavedFiltersButton";
import VibeSearchInput from "@/components/Common/filter/VibeSearchInput";
import CreateGroupWithFiltersModal from "@/components/Groups/CreateGroupWithFiltersModal";
import InfluencerBasicFilters from "@/components/Influencer/influencerNetwork/components/InfluencerBasicFilters";
import InfluencerNetworkGrid from "@/components/Influencer/influencerNetwork/components/InfluencerNetworkGrid";
import SortButton from "@/components/Influencer/influencerNetwork/components/SortButton";
import ViewModeToggle from "@/components/Influencer/influencerNetwork/components/ViewModeToggle";
import useGuardEditMode from "@/hooks/useGuardEditMode";
import useHideIntercom from "@/hooks/useHideIntercom";
import {useSocialNumberFormat} from "@/hooks/useNumberFormat";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useTrialTabMilestone} from "@/hooks/useTrialTabMilestone";
import {useTypeSwitcher} from "@/hooks/useTypeSwitcher";
import {useAgencyId} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {FilterStoreProvider} from "@/store/filterStoreContext";
import useInfluencerNetworkStore from "@/store/influencerNetwork";
import {InfluencerSidebarStoreProvider} from "@/store/influencerSidebarContext";
import {discardFilterChangesModalAtom, editFilterAtom, influencerViewModeAtom} from "@/store/jotaiAtoms";
import type {TCampaignType, TPaginated, TPlatformCounts} from "@/store/types";
import useUserStore from "@/store/user";
import {createFileRoute} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {AnimatePresence, motion} from "framer-motion";
import {useAtom, useAtomValue} from "jotai";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useEvent, useMedia, useTitle, useUnmount} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/influencers-list")({
  component: InfluencerList,
  validateSearch: zodValidator(
    z.object({
      source_tab: z.string().optional(),
    }),
  ),
});

function InfluencerList() {
  const {t} = useTranslation("common");
  const navigate = Route.useNavigate();
  const isSm = useMedia("(min-width: 640px)");
  useTrialTabMilestone("influencers_tab");
  const isXl = useMedia("(min-width: 1280px)");
  const formatNumber = useSocialNumberFormat(true, false);
  const influencerType = useInfluencerNetworkStore(state => state.filters.type);
  const isLoggedOut = useUserStore(state => !state.user.id);
  const shouldFetchOpenCampaigns = useUserStore(
    state => state.user.is_company && state.user.user_type !== "admin" && state.user.owns_open_campaign,
  );
  const agencyId = useAgencyId();
  const companyId = useCurrentCompanyId();
  const activeFilters = useInfluencerNetworkStore(state => state.activeFilters);
  const applyDrawerFilters = useInfluencerNetworkStore(state => state.applyDrawerFilters);
  const buildSearchParams = useInfluencerNetworkStore(state => state.buildSearchParams);
  const cancelDrawerFilters = useInfluencerNetworkStore(state => state.cancelDrawerFilters);
  const filters = useInfluencerNetworkStore(state => state.filters);
  const isFilterDrawerOpen = useInfluencerNetworkStore(state => state.isFilterDrawerOpen);
  const pendingSearchParams = useInfluencerNetworkStore(state => state.pendingSearchParams);
  const openDrawer = useInfluencerNetworkStore(state => state.openDrawer);
  const resetAllFilters = useInfluencerNetworkStore(state => state.resetAllFilters);

  const switchType = useInfluencerNetworkStore(state => state.switchType);
  const searchParams = useInfluencerNetworkStore(state => state.searchParams);
  const setFilters = useInfluencerNetworkStore(state => state.setFilters);
  const setSearchParams = useInfluencerNetworkStore(state => state.setSearchParams);
  const updateActiveFilters = useInfluencerNetworkStore(state => state.updateActiveFilters);
  const setValue = useInfluencerNetworkStore(state => state.setValue);
  const reset = useInfluencerNetworkStore(state => state.reset);
  const [editFilter, setEditFilter] = useAtom(editFilterAtom);
  const discardModalData = useAtomValue(discardFilterChangesModalAtom);
  const influencerNetworkViewMode = useAtomValue(influencerViewModeAtom);
  const [isCreateGroupWithFiltersModalOpen, setCreateGroupWithFiltersModalOpen] = useState(false);
  const [platformCountsByType, setPlatformCountsByType] = useState<TPlatformCounts>({
    instagram_count: 0,
    tiktok_count: 0,
    ugc_count: 0,
  });

  const isInitialCountSet = useRef(false);
  const areFiltersSeededFromCampaigns = useRef(false);
  const guardEditMode = useGuardEditMode();

  const {mutate: updateFilter, isPending: isUpdateFilterPending} = useUpdateInfluencerFilterMutation({
    filterId: editFilter?.filterId,
    onSuccess: () => setEditFilter(null),
  });

  const cancelEditMode = () => {
    if (!editFilter || discardModalData) return;
    guardEditMode(() => {
      setValue("filters", editFilter.snapshotFilters);

      const newSearchParams = buildSearchParams();
      setSearchParams(newSearchParams);
      updateActiveFilters();
      setEditFilter(null);
    });
  };

  useEvent(
    "keydown",
    isFilterDrawerOpen
      ? undefined
      : (e: Event) => {
          if ((e as KeyboardEvent).key === "Escape") cancelEditMode();
        },
  );

  const {typeSwitcher} = useTypeSwitcher({
    className: "max-lg:grow",
    buttonClassName: "flex-1 sm:min-w-0 md:min-w-0",
    onChange: value => {
      if (influencerType !== value) {
        guardEditMode(() => {
          setEditFilter(null);
          switchType(value as TCampaignType);
        });
      }
    },
    options: [
      {count: platformCountsByType.instagram_count, value: "instagram"},
      {count: platformCountsByType.tiktok_count, value: "tiktok"},
      {count: platformCountsByType.ugc_count, value: "ugc"},
    ],
    showLabel: isXl,
    value: influencerType,
  });

  const {data: influencersData, isPlaceholderData: isInfluencersPlaceholder} = useInfluencerListQuery({
    queryOptions: {enabled: false, placeholderData: previousData => previousData},
    searchParams,
  });

  const hasPendingFilterChanges = !!pendingSearchParams && pendingSearchParams !== searchParams;
  const {data: pendingCountData, isFetching: isPendingCountFetching} = useGenericQuery<
    TPaginated<{count?: number | null}>
  >({
    queryKey: INFLUENCER_KEYS.qInfluencerCount(pendingSearchParams),
    enabled: hasPendingFilterChanges,
  });

  const advancedFiltersButton = (
    <AdvancedFiltersButton
      activeFilters={activeFilters}
      fetchedInfluencerCount={influencersData?.count}
      isOpen={isFilterDrawerOpen}
      isPendingCountFetching={isPendingCountFetching}
      onApply={applyDrawerFilters}
      onCancel={cancelDrawerFilters}
      onOpen={openDrawer}
      pendingCount={hasPendingFilterChanges ? pendingCountData?.count : undefined}
      socialType={influencerType}
    />
  );

  const renderResetButton = () => (
    <Button
      buttonStyle="White"
      className="h-10 px-2 text-sm sm:px-5"
      onClick={() => {
        resetAllFilters();
        setEditFilter(null);
      }}>
      <span className="text-neutral-black">{t("reset")}</span>
    </Button>
  );

  const renderEditActions = ({compact}: {compact?: boolean} = {}) => (
    <>
      <Button
        buttonStyle="White"
        className={compact ? "h-10 rounded-lg px-10" : "h-12 flex-1 rounded-lg"}
        onClick={cancelEditMode}>
        {t("cancel")}
      </Button>
      <Button
        buttonStyle="Gradient"
        className={compact ? "h-10 rounded-lg px-10 text-nowrap" : "h-12 flex-1 rounded-lg text-nowrap"}
        isDisabled={!editFilter?.filterName.trim()}
        isLoading={isUpdateFilterPending}
        onClick={() => editFilter && updateFilter({filter_params: filters, name: editFilter.filterName.trim()})}>
        {t("saveChanges")}
      </Button>
    </>
  );

  const renderDesktopFilters = () => (
    <div className="flex w-full flex-col gap-5">
      <div className="flex gap-5">
        {typeSwitcher}
        <div className="grow max-lg:hidden">
          <VibeSearchInput
            onSocialChange={type =>
              guardEditMode(() => {
                setEditFilter(null);
                switchType(type);
              })
            }
          />
        </div>
        <SavedFiltersButton enableActions />
      </div>
      <div className="w-full lg:hidden">
        <VibeSearchInput
          onSocialChange={type =>
            guardEditMode(() => {
              setEditFilter(null);
              switchType(type);
            })
          }
        />
      </div>
      <div className="flex justify-between gap-10">
        <InfluencerBasicFilters />
        <div className="flex items-center gap-2">
          {activeFilters.length > 0 && renderResetButton()}
          {advancedFiltersButton}
        </div>
      </div>
    </div>
  );

  const renderMobileFilters = () => (
    <div className="flex w-full flex-col gap-5">
      <div className="flex justify-between gap-2">{typeSwitcher}</div>
      <VibeSearchInput
        onSocialChange={type =>
          guardEditMode(() => {
            setEditFilter(null);
            switchType(type);
          })
        }
      />
      <div className="flex items-center justify-between gap-4">
        <SavedFiltersButton enableActions />
        <div className="flex items-center gap-2">
          {activeFilters.length > 0 && renderResetButton()}
          {advancedFiltersButton}
        </div>
      </div>
    </div>
  );

  const {data: openCampaigns} = useOpenCampaignsQuery({
    queryOptions: {
      enabled: shouldFetchOpenCampaigns,
      refetchOnMount: true,
    },
  });

  const {data: platformCounts, isPlaceholderData: isPlatformCountsPlaceholder} = useGenericQuery<TPlatformCounts>({
    queryKey: INFLUENCER_KEYS.qInfluencerPlatformCounts(searchParams),
    enabled: !!searchParams,
    placeholderData: previousData => previousData,
  });

  useTitle(t("influencerNetwork"));

  useEffect(() => {
    if (!platformCounts || isPlatformCountsPlaceholder) return;

    if (!isInitialCountSet.current) {
      setPlatformCountsByType(platformCounts);
      isInitialCountSet.current = true;
      return;
    }

    const count = isInfluencersPlaceholder ? undefined : influencersData?.count;
    setPlatformCountsByType(prev => {
      if (influencerType === "instagram") return {...prev, instagram_count: count ?? platformCounts.instagram_count};
      if (influencerType === "tiktok") return {...prev, tiktok_count: count ?? platformCounts.tiktok_count};
      if (influencerType === "ugc") return {...prev, ugc_count: count ?? platformCounts.ugc_count};

      return prev;
    });
  }, [platformCounts, influencerType, isPlatformCountsPlaceholder, influencersData?.count, isInfluencersPlaceholder]);

  useEffect(() => {
    if (openCampaigns) {
      setValue("openCampaignsOfCompany", openCampaigns);

      if (!openCampaigns?.length || areFiltersSeededFromCampaigns.current) return;

      areFiltersSeededFromCampaigns.current = true;

      const locations = new Set<string>();
      const campaignTypes = new Set<TCampaignType>();

      openCampaigns.forEach(campaign => {
        campaign.locations?.forEach(location => locations.add(location));
        if (campaign.ugc) campaignTypes.add("ugc");
        if (campaign.tiktok) campaignTypes.add("tiktok");
        if (!campaign.ugc && !campaign.tiktok) campaignTypes.add("instagram");
      });

      if (locations.size === 1 && locations.has("Switzerland")) {
        setFilters("influencer_country", ["Switzerland"], {skipQuery: true});
      }

      if (locations.size === 1 && locations.has("Germany")) {
        setFilters("influencer_country", ["Germany"], {skipQuery: true});
      }

      if (campaignTypes.size === 1 && campaignTypes.has("ugc")) {
        setFilters("type", "ugc", {skipQuery: true});
      }

      if (campaignTypes.size === 1 && campaignTypes.has("tiktok")) {
        setFilters("type", "tiktok", {skipQuery: true});
      }

      if (campaignTypes.size === 1 && campaignTypes.has("instagram")) {
        setFilters("type", "instagram", {skipQuery: true});
      }
    }
  }, [openCampaigns]);

  useHideIntercom(isFilterDrawerOpen || (!!editFilter && !isSm));

  useUnmount(reset);

  return (
    <>
      <meta name="robots" content="noindex" />
      <FilterStoreProvider store={useInfluencerNetworkStore}>
        <div className={clsxm("bg-neutral-grey-lightest", isLoggedOut ? "pb-10" : "pb-20")}>
          <div className="rounded-b-xl bg-white shadow-sm">
            <div className="max-w-wide container mx-auto flex flex-col items-start gap-5 px-8 py-8">
              {isSm ? renderDesktopFilters() : renderMobileFilters()}
            </div>
          </div>
          <div
            className={clsxm(
              "max-w-wide container mx-auto flex flex-col items-start gap-10 px-8 py-10",
              influencerNetworkViewMode === "list" && "px-3 py-5",
            )}>
            <div
              className={clsxm(
                "flex w-full flex-col items-start gap-4 rounded-xl bg-white p-5",
                influencerNetworkViewMode === "grid" && "contents",
              )}>
              <div
                className={clsxm(
                  "relative flex w-full flex-col justify-between gap-2 lg:flex-row lg:items-center",
                  editFilter && "flex flex-col items-start gap-2 lg:grid lg:grid-cols-[1fr_auto_auto] xl:grid-cols-3",
                )}>
                <div className="flex max-w-full min-w-0 items-center gap-2 overflow-hidden md:gap-5">
                  {editFilter ? (
                    <div className="relative min-w-10 px-1">
                      <span className="text-2.5xl invisible block leading-8 font-semibold whitespace-pre">
                        {editFilter?.filterName || " "}
                      </span>
                      <input
                        className="text-neutral-black text-2.5xl absolute inset-0 border-b border-b-current leading-8 font-semibold outline-none"
                        value={editFilter?.filterName ?? ""}
                        onChange={e => editFilter && setEditFilter({...editFilter, filterName: e.target.value})}
                        onKeyDown={e => {
                          if (e.key === "Enter" && editFilter?.filterName.trim()) {
                            updateFilter({filter_params: filters, name: editFilter.filterName.trim()});
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <span className="text-neutral-black text-2.5xl line-clamp-3 leading-8 font-semibold">
                      {t("Our network")}
                    </span>
                  )}
                  {activeFilters.length > 0 && influencersData?.count != null && (
                    <div className="border-primary-light-solid bg-primary-light-solid flex rounded-sm border">
                      <span className="text-2.5xl text-primary-dark-solid flex items-center px-3 py-1.5 leading-7 font-bold">
                        {formatNumber(influencersData.count)}
                      </span>
                      {(companyId || agencyId) && (
                        <Button
                          buttonStyle="White"
                          className="flex h-10.5 items-center gap-2 rounded-[3px] bg-white px-4"
                          onClick={() => setCreateGroupWithFiltersModalOpen(true)}>
                          <FolderPlusIcon className="size-3.5 shrink-0 text-black" />
                          <RightChevronIcon className="text-neutral-black size-2.5 rotate-90" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="contents w-full items-center justify-between gap-3 sm:flex lg:contents">
                  {editFilter && (
                    <div className="hidden items-center justify-center gap-5 sm:flex">
                      {renderEditActions({compact: true})}
                    </div>
                  )}
                  <div className="flex items-center gap-5 sm:justify-end">
                    <ViewModeToggle atom={influencerViewModeAtom} />
                    {!filters.vibe && (
                      <SortButton
                        ClosedIconComponent={<DownUpArrowIcon className="text-neutral-grey-dark size-4.5" />}
                        OpenedIconComponent={<DownUpArrowIcon className="text-primary-dark-solid size-4.5" />}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="w-full">
                <InfluencerSidebarStoreProvider>
                  <InfluencerNetworkGrid />
                </InfluencerSidebarStoreProvider>
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {editFilter && !isSm && (
            <motion.div
              key="edit-bottom-sheet"
              animate={{y: 0}}
              className="border-soft-grey-500 fixed right-0 bottom-0 left-0 z-90 flex flex-col rounded-t-xl border-t bg-white shadow-md"
              exit={{y: "100%"}}
              initial={{y: "100%"}}
              transition={{duration: 0.25, ease: "easeOut"}}>
              <div className="bg-neutral-grey-light mx-auto mt-4 mb-2 h-1 w-10 rounded-full" />
              <div className="flex w-full flex-col gap-4 px-3 pb-6">
                <div className="flex gap-2">{renderEditActions()}</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {isLoggedOut && (
          <div className="border-neutral-white-dark mx-auto mb-10 flex w-min flex-col items-center justify-center gap-5 self-center rounded-lg border p-6 shadow-lg">
            <span className="text-neutral-black text-xl font-semibold whitespace-nowrap">
              {t("registerToSeeNetwork")}
            </span>
            <span
              onClick={() => {
                navigate({to: "/register"});
              }}
              className="flex h-12 w-72 cursor-pointer items-center justify-center gap-1 rounded-lg bg-linear-(--button-gradient) font-semibold text-white">
              {t("registerNow")}
            </span>
          </div>
        )}
        {isCreateGroupWithFiltersModalOpen && (
          <CreateGroupWithFiltersModal onClose={() => setCreateGroupWithFiltersModalOpen(false)} />
        )}
      </FilterStoreProvider>
    </>
  );
}
